import { renderSettingsOf } from '../utils/reportPdf.js';
import React, { useEffect, useState } from 'react';
import { apiGet, apiSend } from '../api';
// v0.x — native shell: a _blank anchor has no tab UI in the store wrapper;
// interceptExternalClick routes it to the system browser there and is a
// no-op on the web.
import { interceptExternalClick } from '../utils/nativeShell';
import { theme, card, input, btnPrimary, btnSecondary } from '../theme';
// v0.17.1 — Lucide migration.
import { Check, Copy, ChevronRight, Plus, X } from 'lucide-react';
import { parseEmailList } from '../utils/emailList.js';
import { mergeTeam, groupTeam, matchesPerson, chatForKind, CHAT_CHOICES, seatChats, seatChat, companyNames } from '../utils/shareTeam.js';
import { postSendUpdate, COMMENT_POSTED_EVENT } from './UnsentUpdatesBar.jsx';

// ShareReportModal - compact 480px modal for minting a share link.
// Used from both <ReportScreen> and <SharesScreen>.
//
// Props:
//   projectId  - which project the share is tied to
//   onClose()  - parent closes the modal
//   onCreated(share) - parent receives the new share row (for list refresh)
//
// v0.8.0 - initial. Same visual style as the v0.7.3 defect modal:
//          centred, dimmed backdrop, theme tokens, compact width.
// v0.9.3 - accept reportId prop. When set, the resulting share row anchors
//          to that saved Report (Reports.Id). When null, it's a plain
//          project-level share (default for SharesScreen). ReportScreen passes
//          the current ?reportId=N query through.
// v0.218.0 — renderSettings: the report settings the sender is building the PDF
// with. They go with the share so the recipient's Download PDF is this PDF
// (Brett: "what we produce is what they get"). The plan level is added by the
// server from the session, never from here.
// v0.266.0 — the email field takes several addresses (Brett: "yes it should
// take several addresses"). Each person gets their own link, made the same
// way "Send to all team members" makes them, so a reply says who wrote it and
// one link can be turned off on its own. Anything that is not an address
// stops the send and is named; it is never skipped. One address is the
// single-link flow it always was.
// v0.282.0 — a picker, not a wall of chips (Brett: "Yes share by group ... I
// don't want a wall of contacts. Should be a more streamlined picker"). The
// team is offered in groups by contact type, folded shut, each with Pick all;
// a group picked whole is one pill in Send to. One search box finds people by
// name, company or address, and takes a typed address for someone not on the
// team. Still one link per person.
// v0.283.0 — each link is for one chat (Brett: "Different chat for subbies and
// clients"). A person's contact type picks it: clients and consultants the
// client chat, subcontractors and suppliers the subcontractor chat, staff both.
// Anyone the type does not settle (a builder, Other, a typed address, a link
// with no address) takes the one choice the box asks, the client chat unless
// changed. A person in the wrong chat is fixed on their contact type.
// v0.331.0 — Brett: "If the Jane person is a client consultant they should
// have a client link and not a trade link. Allow me to swap them but would be
// trade by default." Anyone the type does not settle now starts on the
// SUBCONTRACTOR chat, and each of them has their own choice, so one client
// consultant typed in among trades can be swapped to the client chat alone.
// A client link to someone with no seat at the report's client company still
// never puts the report in a client home (api/shared/client-home.js).
// v0.332.0 — Brett: "do it best in class". Once this project's reports are
// stamped for a client company, the box asks the server who sits where before
// Send: a Client link only for someone with a seat there (or our own staff, or
// the sender), Both only for our own staff (or the sender). Anyone else is
// moved to the Subcontractor chat and the box says so by name; their choice
// offers only the chats they may have. Until the answer is in, Send waits; if
// it cannot be read, Send stays off for any link not on the Subcontractor chat
// and the box says why with Try again. The server checks every link again.
// v0.315.0 — "Also send the comments not emailed yet" (Brett: "build the send
// outstanding updates tick"). Shown only when office comments are waiting on
// this project's links; ticked, the send is followed by the same Send update
// as the strip under the header — everyone on the project's links with
// comments waiting gets their one email. A new link has nothing waiting (its
// page shows every comment), so this reaches the people already on links.
// Never queued offline, like the strip's own button.
export default function ShareReportModal({ projectId, reportId = null, onClose, onCreated, renderSettings = null }) {
  // What is in the search box. Holding an "@" it is an address (or a list):
  // Send includes it, and anything in it that is not an address stops the send.
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState(() => new Set());   // team people, by key
  const [extra, setExtra] = useState([]);                   // added addresses not on the team
  const [openGroups, setOpenGroups] = useState(() => new Set());
  // 2026-09-15 — an optional note from the sender, quoted in the email.
  const [message, setMessage] = useState('');
  // v0.265.0 — starts on Reply. Brett, after a View link left his client
  // "no spot to write": "Start it on reply".
  const [permission, setPermission] = useState('reply');
  const [expiresInDays, setExpiresInDays] = useState(30);
  // v0.283.0 - the chat for a link with no address. v0.331.0 - starts on the
  // subcontractor chat; anyone else the type does not settle has their own
  // choice in chatFor (by lower-case address), also starting on it.
  const [otherChat, setOtherChat] = useState('trade');
  const [chatFor, setChatFor] = useState(() => new Map());
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);   // the new share row
  const [copied, setCopied] = useState(false);
  // v0.11.0 - email send toggle. Default ON so the natural flow is
  // "create share -> email sent". User can untick to just get the URL.
  const [sendByEmail, setSendByEmail] = useState(true);
  // v0.11.0 - bulk send-to-whole-team result list.
  const [bulkResults, setBulkResults] = useState(null);
  const [copiedRow, setCopiedRow] = useState(null);
  // v0.315.0 - the comments not emailed yet, and whether this send takes them.
  const [waiting, setWaiting] = useState(null);
  const [sendUpdates, setSendUpdates] = useState(true);
  const [updateOutcome, setUpdateOutcome] = useState(null);   // { pending } | { results, sent, failed } | { error }
  useEffect(() => {
    let cancelled = false;
    apiGet(`/api/report-updates?projectId=${encodeURIComponent(projectId)}`, { fresh: true })
      .then((s) => { if (!cancelled) setWaiting(s && typeof s === 'object' ? s : null); })
      .catch(() => { /* no tick: the strip under the header says what it could not read */ });
    return () => { cancelled = true; };
  }, [projectId]);
  const waitingComments = (waiting && waiting.ready && waiting.comments) || 0;
  const waitingPeople = new Set(((waiting && Array.isArray(waiting.people)) ? waiting.people : [])
    .map((p) => String(p.recipientEmail || '').toLowerCase())).size;
  const offerUpdates = waitingComments > 0 && waitingPeople > 0;

  // v0.10.2 - Team-aware share modal. Loads the per-report team if a
  // reportId is set (so this share goes to people listed on THIS report),
  // otherwise falls back to the project's master team list. Each member
  // becomes a chip that pre-fills the recipient email on click - one
  // click = one share with the right address.
  // v0.281.0 - the people come from the project team page, not the old
  // hidden list (Brett: "Can we use that instead of people typing their name
  // in"). A report share adds the report's distribution for anyone not on the
  // page. Each person picked gets their own link, so the link says who is
  // replying. People on the page with no email are named underneath.
  const [team, setTeam] = useState([]);
  const [noEmail, setNoEmail] = useState([]);
  const [teamLoaded, setTeamLoaded] = useState(false);
  // v0.339.0 — the team is the REPORT's (/api/report-contacts). A share made
  // for the whole location (no report) has no team to pick from: it says so.
  // A team that could not be read is named, never shown as nobody.
  const [teamFailed, setTeamFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!reportId) { setTeam([]); setNoEmail([]); setTeamLoaded(true); return () => { cancelled = true; }; }
    const asked = [
      apiGet(`/api/report-contacts?reportId=${encodeURIComponent(reportId)}`),
      apiGet(`/api/report-team?reportId=${encodeURIComponent(reportId)}`),
    ];
    Promise.allSettled(asked)
      .then((got) => {
        if (cancelled) return;
        setTeamFailed(got[0].status !== 'fulfilled');
        const { people, missing } = mergeTeam(got.map((g) => (g.status === 'fulfilled' ? g.value : [])));
        setTeam(people);
        setNoEmail(missing);
      })
      .finally(() => { if (!cancelled) setTeamLoaded(true); });
    return () => { cancelled = true; };
  }, [projectId, reportId]);

  const groups = groupTeam(team);
  const onTeam = new Map(team.map((p) => [p.key, p]));
  const typed = query.includes('@') ? parseEmailList(query) : { emails: [], invalid: [] };
  // Everyone who gets a link: picked people in group order, then added and
  // typed addresses. One per address, each with the chat their link is for.
  const recipients = [];
  {
    const seen = new Set();
    const add = (name, email, kind) => {
      const k = email.toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      const byType = chatForKind(kind);
      recipients.push({ name, email, want: byType || chatFor.get(k) || 'trade', byType: !!byType });
    };
    for (const g of groups) for (const p of g.people) if (picked.has(p.key)) add(p.name, p.email, p.kind);
    for (const e of [...extra, ...typed.emails]) {
      const p = onTeam.get(e.toLowerCase());
      add(p?.name, e, p?.kind);
    }
  }
  const count = recipients.length;

  // v0.332.0 - who sits where, read fresh for these addresses (after the
  // typing stops). seats.key says which addresses the answer is for, so an
  // answer for an older set is never used.
  const seatKey = recipients.map((r) => r.email.toLowerCase()).sort().join(',');
  const [seats, setSeats] = useState({ state: 'loading', key: null });
  const [seatTry, setSeatTry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setSeats((cur) => (cur.key === seatKey && cur.state === 'ok' ? cur : { state: 'loading', key: seatKey }));
    const t = setTimeout(async () => {
      const list = seatKey ? seatKey.split(',') : [];
      const chunks = [];
      for (let i = 0; i < list.length; i += 50) chunks.push(list.slice(i, i + 50));
      if (!chunks.length) chunks.push([]);
      try {
        const got = await Promise.all(chunks.map((c) => apiGet(
          `/api/report-shares?seatsFor=${encodeURIComponent(c.join(','))}&projectId=${encodeURIComponent(projectId)}`,
          { fresh: true })));
        if (cancelled) return;
        const ans = { state: 'ok', key: seatKey, companies: [], people: {}, you: null };
        for (const g of got) {
          if (!g || !Array.isArray(g.companies)) throw new Error('DropPin could not tell who sits at the client company.');
          ans.companies = g.companies;
          ans.you = g.you || ans.you;
          Object.assign(ans.people, g.people || {});
        }
        setSeats(ans);
      } catch (e) {
        if (!cancelled) setSeats({ state: 'failed', key: seatKey, error: (e && e.body && e.body.error)
          || (e && e.status ? `DropPin could not check who sits at the client company (it answered ${e.status}).`
            : 'No connection, so DropPin could not check who sits at the client company.') });
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [projectId, seatKey, seatTry]);
  const seatsOk = seats.state === 'ok' && seats.key === seatKey;
  const stamped = seatsOk && seats.companies.length > 0;
  const firm = seatsOk ? companyNames(seats) : '';
  for (const r of recipients) {
    r.allowed = seatsOk ? seatChats(seats, r.email) : null;
    const made = seatChat(r.want, r.allowed);
    r.chat = made.chat;
    r.moved = made.moved;
  }
  const linkAllowed = seatsOk ? seatChats(seats, '') : null;
  const linkChat = seatChat(otherChat, linkAllowed).chat;
  const moved = recipients.filter((r) => r.moved);
  // Send waits for the answer only when a link would not be on the
  // Subcontractor chat (a trade link needs no seat).
  const needSeats = count ? recipients.some((r) => r.want !== 'trade') : otherChat !== 'trade';
  const seatsHold = needSeats && !seatsOk;
  const movedWhy = (r) => (r.want === 'all' && r.chat === 'client'
    ? `${r.name || r.email} is not one of your staff, so goes on the Client chat only.`
    : `${r.name || r.email} holds no seat at ${firm}, so goes on the Subcontractor chat.`);
  const chatLabel = (chat) => CHAT_CHOICES.find((c) => c.chat === chat)?.label || CHAT_CHOICES[0].label;
  const settled = CHAT_CHOICES
    .map((c) => ({ ...c, people: recipients.filter((r) => r.byType && r.chat === c.chat) }))
    .filter((c) => c.people.length);
  const unsettled = recipients.filter((r) => !r.byType);
  const setChatOf = (email, chat) => setChatFor((cur) => {
    const next = new Map(cur);
    next.set(String(email).toLowerCase(), chat);
    return next;
  });
  const who = (people) => (people.length === 1 ? people[0].name || people[0].email : `${people.length} people`);
  const newTyped = typed.emails.filter((e) => !onTeam.has(e.toLowerCase()) && !extra.some((x) => x.toLowerCase() === e.toLowerCase()));
  const searching = !!query.trim();
  const shown = searching
    ? groups.map((g) => ({ ...g, people: g.people.filter((p) => matchesPerson(p, query)) })).filter((g) => g.people.length)
    : groups;
  const notAnAddress = (bad) => `Not an email address: ${bad.join(', ')}. Fix or remove ${bad.length === 1 ? 'it' : 'them'}, then try again.`;

  const pick = (keys, on) => setPicked((cur) => {
    const next = new Set(cur);
    for (const k of keys) { if (on) next.add(k); else next.delete(k); }
    return next;
  });
  const toggleGroup = (kind) => setOpenGroups((cur) => {
    const next = new Set(cur);
    if (next.has(kind)) next.delete(kind); else next.add(kind);
    return next;
  });

  // Enter in the search box: an address (or a list) becomes pills, a team
  // member's address picks them; a name search with one hit picks that person.
  function takeQuery() {
    if (!query.trim()) return;
    if (!query.includes('@')) {
      const hits = team.filter((p) => matchesPerson(p, query));
      if (hits.length === 1) { pick([hits[0].key], true); setQuery(''); }
      return;
    }
    if (typed.invalid.length) { setError(notAnAddress(typed.invalid)); return; }
    pick(typed.emails.map((e) => e.toLowerCase()).filter((k) => onTeam.has(k)), true);
    setExtra((cur) => [...cur, ...newTyped]);
    setQuery('');
    setError(null);
  }

  // Send to's pills: a group picked whole is one pill, otherwise each person.
  const pills = [];
  for (const g of groups) {
    const keys = g.people.map((p) => p.key);
    if (keys.length > 1 && keys.every((k) => picked.has(k))) {
      pills.push({ id: `g:${g.kind}`, label: `${g.label} · ${keys.length}`, group: true, remove: () => pick(keys, false) });
      continue;
    }
    for (const p of g.people) {
      if (picked.has(p.key)) pills.push({ id: p.key, label: p.name, title: p.email, remove: () => pick([p.key], false) });
    }
  }
  for (const e of extra) pills.push({ id: `x:${e}`, label: e, remove: () => setExtra((cur) => cur.filter((x) => x !== e)) });

  function shareBody(email, emailIt, chat) {
    return {
      projectId: Number(projectId),
      reportId: reportId ? Number(reportId) : null,
      recipientEmail: email || null,
      permission,
      chat,
      expiresAt: new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toISOString(),
      // v0.11.0 - server sends the URL by email if true AND a
      // recipientEmail is set. Response includes emailSent +
      // emailError so the success view reflects reality.
      sendEmail: !!(emailIt && email),
      message: message.trim() || undefined,
      render: renderSettings ? { settings: renderSettingsOf(renderSettings) } : undefined,
    };
  }

  // v0.266.0 - one link per person, in turn; one person failing does not
  // stop the rest.
  async function sendMany(people, emailThem) {
    const results = [];
    for (const p of people) {
      try {
        const row = await apiSend('POST', '/api/report-shares', shareBody(p.email, emailThem, p.chat));
        results.push({
          name: p.name || p.email,
          email: p.email,
          chat: row.chat || p.chat,
          url: row.url,
          emailAsked: !!emailThem,
          emailSent: !!row.emailSent,
          emailError: row.emailError || null,
        });
        if (onCreated) onCreated(row);
      } catch (e) {
        results.push({
          name: p.name || p.email,
          email: p.email,
          chat: p.chat,
          url: null,
          emailAsked: !!emailThem,
          emailSent: false,
          emailError: e.message || String(e),
        });
      }
    }
    setBulkResults(results);
  }

  // v0.315.0 - after the links: the strip's Send update, once.
  async function sendWaitingUpdates() {
    if (!offerUpdates || !sendUpdates) return;
    setUpdateOutcome({ pending: true });
    const out = await postSendUpdate(projectId);
    setUpdateOutcome(out);
    window.dispatchEvent(new Event(COMMENT_POSTED_EVENT));
  }

  async function createShare() {
    setError(null);
    if (typed.invalid.length) {
      setError(notAnAddress(typed.invalid));
      return;
    }
    if (seatsHold) return;
    setCreating(true);
    try {
      if (count > 1) {
        await sendMany(recipients, sendByEmail);
        await sendWaitingUpdates();
        return;
      }
      const chat = recipients[0]?.chat || linkChat;
      const row = await apiSend('POST', '/api/report-shares', shareBody(recipients[0]?.email, sendByEmail, chat));
      setCreated({ ...row, chat: row.chat || chat });
      if (onCreated) onCreated(row);
      await sendWaitingUpdates();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setCreating(false);
    }
  }

  function copyUrl() {
    if (!created?.url) return;
    try {
      navigator.clipboard.writeText(created.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Fallback - select the input. (clipboard API needs HTTPS in some
      // browsers; we just leave the URL visible so users can copy by hand.)
    }
  }

  function copyRowUrl(i, url) {
    try {
      navigator.clipboard.writeText(url);
      setCopiedRow(i);
      setTimeout(() => setCopiedRow((cur) => (cur === i ? null : cur)), 1600);
    } catch { /* clipboard unavailable - the link is shown to copy by hand */ }
  }

  // Backdrop click closes; modal-content click stops propagation.
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          ...card,
          // v0.11.0 - mobile-responsive width + max-height with scroll
          // so the modal works on a phone (the team chips + bulk-
          // results list can grow tall).
          width: '100%', maxWidth: 480,
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: 20,
          boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>
            {bulkResults ? 'Share links ready' : created ? 'Share link ready' : 'Create share link'}
          </h2>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontSize: 18, color: theme.textLight, padding: 0, lineHeight: 1,
          }} title="Close">×</button>
        </div>

        {/* v0.266.0 - the results list comes first: before this it sat behind
            !created, which a bulk send never set, so "Send to all team
            members" sent the emails and left the form on screen. */}
        {!created && !bulkResults ? (
          <>
            {/* v0.282.0 - Send to: the picked pills and one search box. */}
            <div style={{ marginBottom: 12 }}>
              <Label>Send to</Label>
              <div style={{ ...input, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, padding: 6, minHeight: 38 }}>
                {pills.map((p) => (
                  <span key={p.id} title={p.title} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%', minWidth: 0,
                    fontSize: 12, fontWeight: p.group ? 700 : 500, padding: '2px 4px 2px 9px', borderRadius: 999,
                    background: p.group ? 'var(--brand-50)' : 'var(--surface-2)',
                    border: `1px solid ${p.group ? theme.brand : theme.border}`, color: p.group ? theme.brandDark : theme.text,
                  }}>
                    <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{p.label}</span>
                    <button type="button" onClick={p.remove} aria-label={`Remove ${p.label}`} title="Remove" style={{
                      display: 'inline-flex', alignItems: 'center', background: 'transparent', border: 'none',
                      padding: 2, cursor: 'pointer', color: theme.textLight, flexShrink: 0,
                    }}><X size={12} /></button>
                  </span>
                ))}
                <input
                  aria-label="Search the report team or type an email"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); if (error) setError(null); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || ((e.key === ',' || e.key === ';') && query.includes('@'))) { e.preventDefault(); takeQuery(); }
                  }}
                  placeholder="Search name, company or email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', background: 'transparent', color: theme.text, fontSize: 13, fontFamily: 'inherit', padding: '4px 2px' }}
                />
              </div>
              {newTyped.length > 0 && (
                <button type="button" onClick={takeQuery} style={{
                  ...btnSecondary, marginTop: 6, padding: '4px 10px', fontSize: 12, maxWidth: '100%',
                  display: 'inline-flex', alignItems: 'center', gap: 6, textAlign: 'left',
                }}>
                  <Plus size={12} style={{ flexShrink: 0 }} />
                  <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>Add {newTyped.join(', ')}</span>
                </button>
              )}
            </div>

            {/* v0.282.0 - the project team in groups by contact type, folded
                shut (open while searching, or when there is only one). */}
            {teamLoaded && groups.length > 0 && (shown.length > 0 || !query.includes('@')) && (
              <div style={{ marginBottom: 12, border: `1px solid ${theme.border}`, borderRadius: 6, overflow: 'hidden' }}>
                {shown.length === 0 && (
                  <div style={{ padding: '10px 12px', fontSize: 12, color: theme.textLight }}>Nobody on the report team matches.</div>
                )}
                {shown.map((g, i) => {
                  const all = groups.find((x) => x.kind === g.kind).people;
                  const keys = all.map((p) => p.key);
                  const nPicked = keys.filter((k) => picked.has(k)).length;
                  const whole = nPicked === keys.length;
                  const isOpen = searching || groups.length === 1 || openGroups.has(g.kind);
                  return (
                    <div key={g.kind} data-group={g.kind} style={{ borderTop: i === 0 ? 'none' : `1px solid ${theme.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px 4px 4px' }}>
                        <button type="button" aria-expanded={isOpen} onClick={() => toggleGroup(g.kind)} style={{
                          flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 4px',
                          background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                          fontSize: 13, color: theme.text, textAlign: 'left',
                        }}>
                          <ChevronRight size={14} style={{ flexShrink: 0, color: theme.textLight, transform: isOpen ? 'rotate(90deg)' : 'none' }} />
                          <strong>{g.label}</strong>
                          <span style={{ color: theme.textLight }}>{keys.length}</span>
                          {nPicked > 0 && !whole && <span style={{ color: theme.textLight, fontSize: 12 }}>· {nPicked} picked</span>}
                        </button>
                        {!searching && (
                          <button type="button" aria-pressed={whole} onClick={() => pick(keys, !whole)} style={{
                            ...btnSecondary, padding: '4px 10px', fontSize: 12, fontWeight: 600, flexShrink: 0,
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            ...(whole ? { background: 'var(--brand-50)', border: `1px solid ${theme.brand}`, color: theme.brandDark } : {}),
                          }}>{whole ? <><Check size={12} /> All picked</> : 'Pick all'}</button>
                        )}
                      </div>
                      {isOpen && (
                        <div style={{ background: theme.bg, borderTop: `1px solid ${theme.border}`, maxHeight: 220, overflowY: 'auto' }}>
                          {g.people.map((p) => (
                            <label key={p.key} title={p.email} style={{
                              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px 6px 28px',
                              fontSize: 12, color: theme.text, cursor: 'pointer',
                            }}>
                              <input type="checkbox" checked={picked.has(p.key)} onChange={(e) => pick([p.key], e.target.checked)}
                                style={{ width: 14, height: 14, flexShrink: 0, margin: 0, accentColor: theme.brand }} />
                              <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                {p.name}{p.company && <span style={{ color: theme.textLight }}> · {p.company}</span>}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ marginBottom: 12 }}>
              {/* 2026-09-15 — the old hint under the email field (it said nothing would be emailed) was left over
                  from before the email existed, and contradicted the checkbox. */}
              {teamLoaded && !reportId && (
                <div data-state="team-no-report" style={{ fontSize: 11, color: theme.textLight, marginTop: 4 }}>
                  A team belongs to a report. Share from a report to pick its team, or type addresses here.
                </div>
              )}
              {teamLoaded && reportId && teamFailed && (
                <div data-state="team-failed" style={{ fontSize: 11, color: theme.danger, marginTop: 4 }}>
                  This report's team could not be read, so nobody from it is listed. Type addresses, or close and try again.
                </div>
              )}
              {teamLoaded && reportId && !teamFailed && team.length === 0 && (
                <div style={{ fontSize: 11, color: theme.textLight, marginTop: 4 }}>
                  Nobody on this report's team has an email yet. Add people to the report's team to pick them here.
                </div>
              )}
              {teamLoaded && noEmail.length > 0 && (
                <div style={{ fontSize: 11, color: theme.textLight, marginTop: 4 }}>
                  No email on file, so no link: {noEmail.join(', ')}.
                </div>
              )}
            </div>

            {/* v0.283.0 - which chat each link is for: by contact type, one
                choice for the rest. */}
            <div data-chats style={{ marginBottom: 12 }}>
              <Label>Chat</Label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: theme.text }}>
                {settled.map((c) => (
                  <div key={c.chat} data-chat-line={c.chat} style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    <strong>{c.label}</strong>
                    <span style={{ color: theme.textLight }}> · {who(c.people)}</span>
                  </div>
                ))}
                {count === 0 && (
                  <label data-chat-line="choice" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ minWidth: 0, overflowWrap: 'anywhere', color: theme.text }}>This link</span>
                    <select
                      aria-label="Chat for the link"
                      value={linkChat}
                      onChange={(e) => setOtherChat(e.target.value)}
                      style={{ ...input, width: 'auto', padding: '4px 8px', fontSize: 13 }}
                    >
                      {CHAT_CHOICES.filter((c) => !linkAllowed || linkAllowed.includes(c.chat))
                        .map((c) => <option key={c.chat} value={c.chat}>{c.label}</option>)}
                    </select>
                  </label>
                )}
                {/* v0.331.0 - one choice per person the type does not settle,
                    starting on the subcontractor chat. */}
                {unsettled.map((r) => (
                  <label key={r.email} data-chat-line="choice" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ minWidth: 0, overflowWrap: 'anywhere', color: theme.textLight }}>{r.name || r.email}</span>
                    <select
                      aria-label={`Chat for ${r.name || r.email}`}
                      value={r.chat}
                      onChange={(e) => setChatOf(r.email, e.target.value)}
                      style={{ ...input, width: 'auto', padding: '4px 8px', fontSize: 13 }}
                    >
                      {CHAT_CHOICES.filter((c) => !r.allowed || r.allowed.includes(c.chat))
                        .map((c) => <option key={c.chat} value={c.chat}>{c.label}</option>)}
                    </select>
                  </label>
                ))}
                {/* v0.332.0 - the client-seat rule, said by name. */}
                {stamped && count === 0 && (
                  <div data-seat-note="no-address" style={{ fontSize: 12, color: theme.textLight, overflowWrap: 'anywhere' }}>
                    This project's reports are for {firm}. A link with no address can only be for the Subcontractor chat.
                  </div>
                )}
                {moved.map((r) => (
                  <div key={`m:${r.email}`} data-seat-note="moved" style={{ fontSize: 12, color: theme.textLight, overflowWrap: 'anywhere' }}>
                    {movedWhy(r)}
                  </div>
                ))}
                {needSeats && seats.state === 'failed' && seats.key === seatKey && (
                  <div data-seat-note="failed" style={{ fontSize: 12, color: theme.danger, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                      {seats.error} Nothing is sent on a Client or Both chat until it can.
                    </span>
                    <button type="button" onClick={() => setSeatTry((n) => n + 1)} style={{ ...btnSecondary, padding: '4px 10px', fontSize: 12 }}>Try again</button>
                  </div>
                )}
                {seatsHold && seats.state !== 'failed' && (
                  <div data-seat-note="checking" style={{ fontSize: 12, color: theme.textLight }}>
                    Checking who sits at the client company…
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <Label>Message (optional)</Label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={1000}
                rows={3}
                placeholder="Add a note for the recipient"
                style={{ ...input, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>

            <div style={{ marginBottom: 12 }}>
              <Label>Permission</Label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {/* v0.15.0 - 3-tier permissions. View (safe to share
                    widely), Rectify (supervisor can mark items
                    rectified with photo+comment), Owner (also signs
                    off Complete in v0.16.0). v0.265.0 - Reply is the
                    default. */}
                <PillOption
                  label="View only"
                  active={permission === 'view'}
                  onClick={() => setPermission('view')}
                />
                <PillOption
                  label="Reply"
                  active={permission === 'reply'}
                  onClick={() => setPermission('reply')}
                  title="Recipient can reply under each item with a message or photo"
                />
                <PillOption
                  label="Rectify"
                  active={permission === 'rectify'}
                  onClick={() => setPermission('rectify')}
                  title="Supervisor can mark items rectified with photo + comment"
                />
                <PillOption
                  label="Owner sign-off"
                  active={permission === 'owner'}
                  onClick={() => setPermission('owner')}
                  title="Recipient can rectify AND mark items Complete (sign-off)"
                />
              </div>
              <div style={{ fontSize: 11, color: theme.textLight, marginTop: 6 }}>
                {permission === 'view'    && 'Read-only. Recipient sees defects but cannot change anything.'}
                {permission === 'reply'   && 'Recipient can reply under each item with a message or photos, like a chat. Cannot change anything.'}
                {permission === 'rectify' && 'Recipient can mark defects rectified — photo + comment required as evidence. Paid tier.'}
                {permission === 'owner'   && 'Recipient can rectify AND confirm complete (final sign-off). Paid tier.'}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Label>Expires in</Label>
              <select
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(Number(e.target.value))}
                style={input}
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
              </select>
            </div>

            {error && (
              <div style={{ marginBottom: 12, color: theme.danger, fontSize: 12 }}>{error}</div>
            )}

            {/* v0.11.0 - opt in/out of email send on creation. Only
                meaningful when a recipientEmail is set. */}
            <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                id="dp-send-by-email"
                type="checkbox"
                checked={sendByEmail && count > 0}
                disabled={count === 0}
                onChange={(e) => setSendByEmail(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: theme.brand }}
              />
              <label htmlFor="dp-send-by-email" style={{
                fontSize: 13,
                color: count ? theme.text : theme.textLight,
                cursor: count ? 'pointer' : 'not-allowed',
              }}>
                {count > 1 ? 'Email each person their link now' : 'Send the link to this recipient by email now'}
              </label>
            </div>

            {/* v0.315.0 - the comments not emailed yet go with this send. */}
            {offerUpdates && (
              <div data-send-updates style={{ marginTop: -6, marginBottom: 14, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <input
                  id="dp-send-updates"
                  type="checkbox"
                  checked={sendUpdates}
                  onChange={(e) => setSendUpdates(e.target.checked)}
                  style={{ width: 16, height: 16, flexShrink: 0, accentColor: theme.brand }}
                />
                <label htmlFor="dp-send-updates" style={{ fontSize: 13, color: theme.text, cursor: 'pointer', minWidth: 0, overflowWrap: 'anywhere' }}>
                  Also send the {plural(waitingComments, 'comment', 'comments')} not emailed yet
                  <span style={{ color: theme.textLight }}> — to {plural(waitingPeople, 'person', 'people')} on this project's links</span>
                </label>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button onClick={onClose} style={btnSecondary}>Cancel</button>
              <button
                onClick={createShare}
                disabled={creating || seatsHold}
                style={{ ...btnPrimary, opacity: creating || seatsHold ? 0.5 : 1 }}
              >
                {creating
                  ? (count > 1 ? `Creating ${count}…` : 'Creating…')
                  : sendByEmail && count
                    ? (count > 1 ? `Send ${count} links` : 'Send link')
                    : (count > 1 ? `Create ${count} links` : 'Create share link')}
              </button>
            </div>
          </>
        ) : bulkResults ? (
          <>
            {/* v0.11.0 - bulk results. One row per person with a small
                sent/failed pill and the URL fallback. v0.266.0 - LINK when
                no email was asked for, with a Copy on each row. */}
            <div style={{ fontSize: 13, marginBottom: 12, color: theme.text }}>
              {bulkResults.some(r => r.emailAsked)
                ? `Done. ${bulkResults.filter(r => r.emailSent).length} of ${bulkResults.length} emails sent.`
                : `${bulkResults.filter(r => r.url).length} of ${bulkResults.length} links made. Copy each one to send it yourself.`}
            </div>
            <div style={{ border: `1px solid ${theme.border}`, borderRadius: 6, overflow: 'hidden' }}>
              {bulkResults.map((r, i) => (
                <div key={i} style={{
                  padding: '8px 10px',
                  fontSize: 12,
                  borderTop: i === 0 ? 'none' : `1px solid ${theme.border}`,
                  background: r.emailSent ? theme.bg : (r.emailError ? theme.bg : theme.surface),
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <strong style={{ color: theme.text, minWidth: 0, overflowWrap: 'anywhere' }}>{r.name}</strong>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, flexShrink: 0,
                      background: r.emailSent ? theme.success : (r.emailAsked || !r.url) ? theme.danger : theme.textLight, color: '#fff',
                    }}>{r.emailSent ? 'SENT' : (r.emailAsked || !r.url) ? 'FAILED' : 'LINK'}</span>
                  </div>
                  <div data-result-chat={r.chat} style={{ color: theme.textLight, marginTop: 2, overflowWrap: 'anywhere' }}>
                    {r.name !== r.email ? `${r.email} · ` : ''}{chatLabel(r.chat)}
                  </div>
                  {r.emailError && (
                    <div style={{ color: theme.danger, marginTop: 4, fontSize: 11 }}>{r.emailError}</div>
                  )}
                  {r.url && (
                    <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <a href={r.url} target="_blank" rel="noreferrer" onClick={interceptExternalClick}
                        style={{ color: theme.brand, fontFamily: 'monospace', fontSize: 10, wordBreak: 'break-all', minWidth: 0 }}>{r.url}</a>
                      <button onClick={() => copyRowUrl(i, r.url)} title="Copy link" aria-label={`Copy the link for ${r.email}`}
                        style={{ ...btnSecondary, padding: '3px 7px', fontSize: 11, display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                        {copiedRow === i ? <Check size={12} /> : <Copy size={12} />}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <UpdateOutcome outcome={updateOutcome} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button onClick={onClose} style={btnPrimary}>Done</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 14, fontSize: 13, color: theme.text }}>
              {created.emailSent
                ? `Sent to ${created.recipientEmail}. They can view the report without signing in.`
                : created.emailError
                  ? `Share link minted, but the email could not be sent: ${created.emailError}. Use the URL below to send manually.`
                  : `Send this link to ${created.recipientEmail || 'the recipient'}. They can view the report without signing in.`}
            </div>
            <div style={{ marginBottom: 14 }}>
              <Label>Public URL</Label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  readOnly
                  value={created.url}
                  style={{ ...input, fontFamily: 'monospace', fontSize: 12 }}
                  onFocus={(e) => e.target.select()}
                />
                <button onClick={copyUrl} style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }} title="Copy to clipboard">
                  {copied ? <><Check size={14} /> Copied</> : 'Copy'}
                </button>
              </div>
              <div data-created-chat={created.chat} style={{ fontSize: 11, color: theme.textLight, marginTop: 6 }}>
                {chatLabel(created.chat)} · Expires {new Date(created.expiresAt).toLocaleString('en-AU', {
                  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                })}.
              </div>
            </div>
            <UpdateOutcome outcome={updateOutcome} bottom={14} />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={btnPrimary}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function plural(n, one, many) { return `${n} ${n === 1 ? one : many}`; }

// v0.315.0 - what the tick's Send update did: sending, how many went, who
// failed and why, or why nothing was sent.
function UpdateOutcome({ outcome, bottom = 0 }) {
  if (!outcome) return null;
  const box = { marginTop: 12, marginBottom: bottom, fontSize: 12, overflowWrap: 'anywhere' };
  if (outcome.pending) return <div data-update-outcome="sending" style={{ ...box, color: theme.textLight }}>Sending the comments not emailed yet…</div>;
  if (outcome.error) {
    return (
      <div data-update-outcome="error" style={{ ...box, color: theme.danger }}>
        Comments not emailed yet were not sent: {outcome.error}
      </div>
    );
  }
  const results = Array.isArray(outcome.results) ? outcome.results : [];
  const failed = results.filter((r) => !r.sent);
  return (
    <div data-update-outcome={failed.length ? 'some-failed' : 'sent'} style={{ ...box, color: theme.text }}>
      Comments not emailed yet: {results.length
        ? `${results.length - failed.length} of ${plural(results.length, 'email', 'emails')} sent.`
        : 'none left to send.'}
      {failed.map((r, i) => (
        <div key={i} style={{ color: theme.danger, marginTop: 2 }}>
          ✗ {r.recipientEmail}{r.error ? `: ${r.error}` : ''}
        </div>
      ))}
    </div>
  );
}

function Label({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 600, color: theme.textLight,
      textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5,
    }}>{children}</div>
  );
}

function PillOption({ label, active, onClick, disabled, title }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
      style={{
        flex: 1,
        fontSize: 12, fontWeight: 600,
        padding: '8px 10px',
        border: `1px solid ${active ? theme.brand : theme.border}`,
        background: active ? theme.bg : theme.surface,
        color: disabled ? theme.textLight : theme.text,
        borderRadius: 6,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}
    >{label}</button>
  );
}
